"use client";

import { useState, useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { ArrowRight, ChevronLeft, Sparkles } from "lucide-react";
import Link from "next/link";

const MIN_TICKETS = 50;
const MAX_TICKETS = 2000;
const DEFAULT_TICKETS = 500;
const MIN_FEE = 0.5;
const MAX_FEE = 10;
const DEFAULT_FEE = 3;

// Example calculator only: the visitor sets the per-ticket platform fee they
// pay today. We don't quote any specific competitor's rates.
function calcOtherPlatformFees(tickets: number, feePerTicket: number) {
  return tickets * feePerTicket;
}

function useOdometer(target: number, durationMs = 700) {
  const [display, setDisplay] = useState(target);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const start = display;
    const delta = target - start;
    const startedAt = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startedAt;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(start + delta * eased);
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, durationMs]);

  return display;
}

// Auto-incrementing live counter (decorative — simulates real-time sales feed)
function useLiveTick(target: number, durationMs = 4500) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let frame: number;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durationMs);
      // ease-out cubic for that "settling" tick feel
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(target * eased);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs]);
  return val;
}

function formatCurrency(n: number) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Math.round(n));
}

export default function EventTicketingHero() {
  const { t } = useLang();
  const [tickets, setTickets] = useState(DEFAULT_TICKETS);
  const [fee, setFee] = useState(DEFAULT_FEE);

  const otherPlatformFees = calcOtherPlatformFees(tickets, fee);
  const savings = otherPlatformFees;

  const otherPlatformDisplay = useOdometer(otherPlatformFees);
  const savingsDisplay = useOdometer(savings);
  const settleControls = useAnimation();

  // Live counter values (decorative, for the floating dashboard)
  const liveGA = useLiveTick(234);
  const liveVIP = useLiveTick(18);
  const liveRevenue = useLiveTick(9720);

  const handleRelease = () => {
    settleControls.start({
      scale: [1, 1.04, 1],
      transition: { duration: 0.45, ease: [0.21, 0.47, 0.32, 0.98] },
    });
  };

  return (
    <section className="relative pt-32 pb-24 px-6 overflow-hidden bg-[#0A0814] text-white">
      {/* Video — base layer */}
      <video
        autoPlay
        muted
        loop
        playsInline
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      >
        <source src="/videos/feature-event-ticketing.mp4" type="video/mp4" />
      </video>
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-[#0A0814]/75 pointer-events-none" />
      {/* Nightlife atmosphere — violet + pink + amber glow blobs */}
      <div
        className="absolute top-1/4 -left-32 w-[800px] h-[800px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(124,58,237,0.28) 0%, transparent 60%)" }}
      />
      <div
        className="absolute bottom-0 -right-32 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.20) 0%, transparent 60%)" }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full pointer-events-none opacity-40"
        style={{ background: "radial-gradient(circle, rgba(251,146,60,0.12) 0%, transparent 70%)" }}
      />
      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative max-w-[1280px] mx-auto">
        <Link
          href="/features"
          className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/55 hover:text-white transition-colors mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {t("All features", "Todas las features")}
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-[11px] font-black tracking-widest uppercase mb-6 text-center"
          style={{ color: "#F472B6" }}
        >
          {t("Direct ticketing · No per-ticket platform fee", "Ticketing directo · Sin fee de plataforma por ticket")}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-center text-[44px] sm:text-6xl md:text-[88px] lg:text-[110px] font-black leading-[0.95] tracking-[-0.04em] mb-6"
        >
          {t("$0 platform fee", "$0 de fee de plataforma")}
          <br />
          <span
            style={{
              background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 45%, #FB923C 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            {t("per ticket.", "por ticket.")}
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center text-[17px] md:text-[20px] font-medium text-white/60 max-w-[680px] mx-auto leading-relaxed mb-16"
        >
          {t(
            "Sell tickets from your own venue site. Apple Pay, branded checkout, live sales counter. Ticketing platforms usually add a fee to every ticket — Redy charges no platform fee per ticket. Your plan covers it.",
            "Vende tickets desde el sitio de tu venue. Apple Pay, checkout con tu marca, contador de ventas en vivo. Las plataformas de tickets suelen sumar un fee a cada entrada — Redy no cobra fee de plataforma por ticket. Lo cubre tu plan."
          )}
        </motion.p>

        {/* Hero showcase: iPhone branded checkout + live dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative mb-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-center max-w-[1100px] mx-auto">
            {/* iPhone branded checkout */}
            <div className="lg:col-span-7 flex justify-center lg:justify-end relative">
              <div
                className="absolute -inset-10 rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 65%)" }}
              />
              <IPhoneFrame>
                <div className="h-full flex flex-col bg-[#0A0814] text-white">
                  {/* Status bar */}
                  <div className="h-7 flex items-center justify-between px-4 text-[8px] font-bold">
                    <span>11:32</span>
                    <span>5G</span>
                  </div>
                  {/* Hero video placeholder w/ gradient */}
                  <div
                    className="h-[110px] relative overflow-hidden flex items-end p-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #7C3AED 0%, #F472B6 60%, #FB923C 100%)",
                    }}
                  >
                    <div className="absolute inset-0 opacity-30" style={{
                      backgroundImage:
                        "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.3) 0%, transparent 50%)",
                    }} />
                    <div className="relative">
                      <div className="text-[8px] font-bold uppercase tracking-widest text-white/80">
                        Friday Dec 13
                      </div>
                      <div className="text-[15px] font-black text-white leading-tight tracking-tight">
                        NEON RUSH
                      </div>
                      <div className="text-[8px] font-medium text-white/70 mt-0.5">
                        Doors 10 PM · 21+
                      </div>
                    </div>
                  </div>
                  {/* Ticket tiers */}
                  <div className="px-3 py-2.5 flex-1 space-y-1.5">
                    {[
                      { name: "General Admission", price: "$40", remaining: "234 sold", selected: true },
                      { name: "VIP Table — Booth", price: "$1,200", remaining: "Only 6 left", selected: false },
                      { name: "Bottle Service", price: "$2,800", remaining: "12 of 12", selected: false },
                    ].map((tier, i) => (
                      <div
                        key={i}
                        className={`rounded-[10px] p-2 ${
                          tier.selected
                            ? "border"
                            : "bg-white/[0.03] border border-white/5"
                        }`}
                        style={
                          tier.selected
                            ? { backgroundColor: "rgba(244,114,182,0.12)", borderColor: "rgba(244,114,182,0.4)" }
                            : undefined
                        }
                      >
                        <div className="flex justify-between items-baseline">
                          <div className="text-[10px] font-black text-white truncate">{tier.name}</div>
                          <div className="text-[11px] font-black text-white tabular-nums shrink-0">{tier.price}</div>
                        </div>
                        <div
                          className="text-[8px] font-bold uppercase tracking-wider mt-0.5"
                          style={{ color: tier.selected ? "#F472B6" : "rgba(255,255,255,0.4)" }}
                        >
                          {tier.remaining}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Apple Pay button */}
                  <div className="px-3 pb-3 pt-1">
                    <div className="bg-black rounded-full py-2.5 flex items-center justify-center gap-1.5">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <text x="0" y="18" fontSize="14" fontFamily="-apple-system,sans-serif" fontWeight="700">

                        </text>
                      </svg>
                      <span className="text-[11px] font-bold text-white">{t("Pay", "Pay")}</span>
                    </div>
                  </div>
                </div>
              </IPhoneFrame>
            </div>

            {/* Live dashboard glassmorphism card */}
            <div className="lg:col-span-5 flex justify-center lg:justify-start">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7, delay: 0.7 }}
                className="relative bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[24px] p-6 w-full max-w-[340px] shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
                    {t("Live sales", "Ventas en vivo")}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: "#F472B6" }} />
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#F472B6" }}>
                      Live
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1">
                      {t("General Admission", "General Admission")}
                    </div>
                    <div className="text-[32px] font-black tabular-nums text-white leading-none">
                      {Math.round(liveGA).toLocaleString()}
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1">
                      {t("VIP Tables", "Mesas VIP")}
                    </div>
                    <div className="text-[32px] font-black tabular-nums leading-none" style={{ color: "#C4B5FD" }}>
                      {Math.round(liveVIP)}
                    </div>
                  </div>

                  <div className="pt-4 border-t border-white/10">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-white/40 mb-1">
                      {t("Revenue", "Ingreso")}
                    </div>
                    <div
                      className="text-[36px] font-black tabular-nums leading-none"
                      style={{
                        background: "linear-gradient(135deg, #F472B6 0%, #FB923C 100%)",
                        WebkitBackgroundClip: "text",
                        backgroundClip: "text",
                        color: "transparent",
                      }}
                    >
                      ${Math.round(liveRevenue).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2 text-[10px] font-bold text-white/55">
                  <Sparkles className="w-3 h-3" style={{ color: "#F472B6" }} />
                  {t("Friday · 11:32 PM · selling", "Viernes · 11:32 PM · vendiendo")}
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Calculator card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative max-w-[920px] mx-auto"
        >
          <div
            className="absolute -inset-4 rounded-[40px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(124,58,237,0.20) 0%, transparent 65%)" }}
          />

          <motion.div
            animate={settleControls}
            className="relative bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[32px] p-8 md:p-12"
          >
            <div className="text-[10px] font-black tracking-widest uppercase text-white/55 mb-2">
              {t("Per event you sell", "Por cada evento que vendes")}
            </div>

            {/* Tickets slider */}
            <div className="mb-7">
              <div className="flex items-end justify-between mb-3">
                <div className="text-[28px] md:text-[36px] font-black tracking-tight leading-none tabular-nums">
                  {tickets.toLocaleString()}
                  <span className="text-[14px] font-bold text-white/45 ml-2">
                    {t("tickets", "tickets")}
                  </span>
                </div>
              </div>
              <input
                type="range"
                min={MIN_TICKETS}
                max={MAX_TICKETS}
                step={10}
                value={tickets}
                onChange={(e) => setTickets(Number(e.target.value))}
                onMouseUp={handleRelease}
                onTouchEnd={handleRelease}
                className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-[#7C3AED] to-[#F472B6]/30 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#F472B6] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab active:[&::-webkit-slider-thumb]:cursor-grabbing"
              />
              <div className="flex justify-between text-[10px] font-bold text-white/35 mt-1.5">
                <span>{MIN_TICKETS}</span>
                <span>{MAX_TICKETS}+</span>
              </div>
            </div>

            {/* Fee-per-ticket slider (what the visitor pays today) */}
            <div className="mb-8">
              <div className="flex items-end justify-between mb-3">
                <div className="text-[28px] md:text-[36px] font-black tracking-tight leading-none tabular-nums">
                  ${fee.toFixed(2)}
                  <span className="text-[14px] font-bold text-white/45 ml-2">
                    {t("platform fee per ticket, today", "fee de plataforma por ticket, hoy")}
                  </span>
                </div>
              </div>
              <input
                type="range"
                min={MIN_FEE}
                max={MAX_FEE}
                step={0.25}
                value={fee}
                onChange={(e) => setFee(Number(e.target.value))}
                onMouseUp={handleRelease}
                onTouchEnd={handleRelease}
                className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gradient-to-r from-[#7C3AED] to-[#F472B6]/30 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-6 [&::-webkit-slider-thumb]:h-6 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:border-[3px] [&::-webkit-slider-thumb]:border-[#F472B6] [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:cursor-grab active:[&::-webkit-slider-thumb]:cursor-grabbing"
              />
              <div className="flex justify-between text-[10px] font-bold text-white/35 mt-1.5">
                <span>${MIN_FEE.toFixed(2)}</span>
                <span>${MAX_FEE.toFixed(2)}+</span>
              </div>
            </div>

            <div className="h-px bg-white/10 mb-8" />

            {/* Compare: other platform vs Redy */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 mb-8">
              <div>
                <div className="text-[10px] font-black tracking-widest uppercase text-white/45 mb-3">
                  {t("Platform fees today", "Fees de plataforma hoy")}
                </div>
                <div className="text-[40px] md:text-[52px] font-black tracking-tight text-white/40 leading-none tabular-nums">
                  −{formatCurrency(otherPlatformDisplay)}
                </div>
                <div className="text-[12px] font-medium text-white/45 mt-2">
                  {t("Your fee per ticket × tickets sold.", "Tu fee por ticket × tickets vendidos.")}
                </div>
              </div>

              <div className="relative">
                <div
                  className="absolute -inset-x-4 -inset-y-3 rounded-[20px] pointer-events-none"
                  style={{ background: "radial-gradient(ellipse, rgba(244,114,182,0.12) 0%, transparent 70%)" }}
                />
                <div className="relative">
                  <div className="text-[10px] font-black tracking-widest uppercase mb-3" style={{ color: "#F472B6" }}>
                    {t("Redy platform fee", "Fee de plataforma Redy")}
                  </div>
                  <div
                    className="text-[40px] md:text-[52px] font-black tracking-tight leading-none tabular-nums"
                    style={{
                      background: "linear-gradient(135deg, #C4B5FD 0%, #F472B6 100%)",
                      WebkitBackgroundClip: "text",
                      backgroundClip: "text",
                      color: "transparent",
                    }}
                  >
                    $0
                  </div>
                  <div className="text-[12px] font-medium mt-2" style={{ color: "#C4B5FD", opacity: 0.75 }}>
                    {t("No platform fee per ticket.", "Sin fee de plataforma por ticket.")}
                  </div>
                </div>
              </div>
            </div>

            {/* Savings hero */}
            <div
              className="rounded-[24px] px-6 py-6 md:px-8 md:py-8 text-white shadow-2xl"
              style={{
                background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
                boxShadow: "0 20px 60px rgba(124, 58, 237, 0.45)",
              }}
            >
              <div className="flex items-start md:items-center justify-between gap-4 flex-col md:flex-row">
                <div>
                  <div className="text-[11px] font-black tracking-widest uppercase text-white/75 mb-2">
                    {t("Per show, stays with you", "Por show, se queda contigo")}
                  </div>
                  <div className="text-[48px] md:text-[64px] font-black leading-none tabular-nums">
                    +{formatCurrency(savingsDisplay)}
                  </div>
                  <div className="text-[13px] font-bold text-white/85 mt-1">
                    {t("On a 12-event year:", "En un año de 12 eventos:")}{" "}
                    <span className="tabular-nums">{formatCurrency(savingsDisplay * 12)}</span>
                  </div>
                </div>
                <Link
                  href="/signup?vertical=nightlife"
                  className="inline-flex items-center gap-2 bg-white font-bold text-[14px] px-6 py-3.5 rounded-full hover:scale-[1.03] transition-transform shadow-lg whitespace-nowrap text-[#0A0814]"
                >
                  {t("Calculate your savings", "Calcula tu ahorro")}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.0 }}
          className="text-center mt-8 text-[12px] font-medium text-white/40"
        >
          {t(
            "Example only, based on the per-ticket fee you set above — check what your current platform actually charges. Card processing applies to card payments on any platform, including Redy.",
            "Solo un ejemplo, basado en el fee por ticket que pongas arriba — revisa lo que cobra tu plataforma actual. El procesamiento de tarjeta aplica a los pagos con tarjeta en cualquier plataforma, incluido Redy."
          )}
        </motion.div>
      </div>
    </section>
  );
}

function IPhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="w-[260px] h-[520px] bg-[#0a0a0a] rounded-[40px] p-[6px] shadow-[0_40px_100px_rgba(0,0,0,0.6)] relative">
      <div className="w-full h-full rounded-[34px] overflow-hidden bg-black relative">
        {/* Notch */}
        <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-4 bg-[#0a0a0a] rounded-full z-10" />
        {children}
      </div>
    </div>
  );
}
